import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { UserPlus, Trash2, Shield, Heart, Users, Share2 } from 'lucide-react';
import InviteFriendsDialog from './InviteFriendsDialog';

const ROLE_LABELS = {
  patient: 'Patient',
  care_partner: 'Care Partner',
  care_coordinator: 'Care Partner',
  supporter: 'Supporter',
  member: 'Supporter',
  // Legacy fallbacks
  inner: 'Patient',
  primary: 'Care Partner',
  community: 'Supporter',
};

const ROLE_COLORS = {
  patient: 'bg-cyan-600',
  care_partner: 'bg-emerald-600',
  care_coordinator: 'bg-emerald-600',
  supporter: 'bg-amber-600',
  member: 'bg-amber-600',
  inner: 'bg-cyan-600',
  primary: 'bg-emerald-600',
  community: 'bg-amber-600',
};

const getRole = (member) => member.role || member.circle_role || member.ring_level || 'supporter';
const getRoleLabel = (member) => ROLE_LABELS[getRole(member)] || 'Member';
const getRoleColor = (member) => ROLE_COLORS[getRole(member)] || 'bg-gray-600';
const isCarePartnerRole = (role) => ['care_partner', 'care_coordinator', 'primary'].includes(role);
const isPatientRole = (role) => ['patient', 'inner'].includes(role);

const CircleMembersTab = ({ members, user, circle, newMember, setNewMember, handleAddMember, handleRemoveMember, addMemberDialogOpen, setAddMemberDialogOpen, onMembersChanged }) => {
  const [updatingRole, setUpdatingRole] = useState(null);
  const [careProfiles, setCareProfiles] = useState({});
  const [shareOpen, setShareOpen] = useState(false);

  React.useEffect(() => {
    if (!circle?.id) return;
    axios.get(`${API}/circles/${circle.id}/assessments/care_profile/results`)
      .then(res => {
        const map = {};
        res.data.forEach(r => { if (r.user?.id && r.primary) map[r.user.id] = r; });
        setCareProfiles(map);
      })
      .catch(() => {});
  }, [circle?.id]);
  const currentMember = members.find(m => m.user_id === user.id);
  const currentRole = currentMember ? getRole(currentMember) : null;
  const isOwner = circle?.patient_id === user.id || circle?.created_by === user.id;
  const canManage = isOwner || isPatientRole(currentRole) || isCarePartnerRole(currentRole);

  const handleRoleChange = async (member, newRoleValue) => {
    const memberId = member.user_id || member.user?.id;
    setUpdatingRole(memberId);
    try {
      const res = await axios.put(`${API}/circles/${circle.id}/members/${memberId}/role`, { role: newRoleValue });
      toast.success(res.data.message);
      if (onMembersChanged) onMembersChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update role'); }
    setUpdatingRole(null);
  };

  return (
    <div className="space-y-6">
      {/* Big obvious invite section — same rich flow as Home page */}
      {canManage && (
        <Card className="surface-card border-2 border-cyan-100 dark:border-cyan-900/40 bg-gradient-to-br from-cyan-50/50 to-transparent dark:from-cyan-950/20" data-testid="squad-invite-section">
          <CardContent className="pt-5 pb-5 px-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-11 h-11 bg-cyan-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <UserPlus className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-base font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Add people to your circle</p>
                  <p className="text-xs text-muted-foreground">Text, WhatsApp, email — share an invite in one tap.</p>
                </div>
              </div>
              <Button onClick={() => setShareOpen(true)} className="btn-primary w-full sm:w-auto" data-testid="squad-share-invite-btn">
                <Share2 className="w-4 h-4 mr-2" /> Share invite
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <InviteFriendsDialog open={shareOpen} onOpenChange={setShareOpen} circle={circle} userName={user?.name} />

      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-foreground">Circle Members</h3>
        {canManage && (
          <Dialog open={addMemberDialogOpen} onOpenChange={setAddMemberDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-member-btn"><UserPlus className="w-4 h-4 mr-2" /> Invite by email</Button>
            </DialogTrigger>
            <DialogContent className="surface-card">
              <DialogHeader>
                <DialogTitle>Invite Circle Member</DialogTitle>
                <DialogDescription>Send an invitation to join this care circle.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddMember} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="member-email">Email Address</Label>
                  <Input id="member-email" type="email" data-testid="member-email-input" placeholder="Enter their email" value={newMember.user_email} onChange={(e) => setNewMember({ ...newMember, user_email: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={newMember.circle_role} onValueChange={(value) => setNewMember({ ...newMember, circle_role: value })}>
                    <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="supporter">Supporter</SelectItem>
                      <SelectItem value="care_coordinator">Care Coordinator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-4 text-sm text-foreground">
                  <p className="font-medium mb-2">Roles:</p>
                  <ul className="space-y-1.5 text-xs text-muted-foreground">
                    <li className="flex items-start gap-2"><Shield className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> <span><strong>Care Coordinator:</strong> Helps manage tasks, updates, and coordination</span></li>
                    <li className="flex items-start gap-2"><Users className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" /> <span><strong>Supporter:</strong> Receives and claims tasks, contributes updates</span></li>
                  </ul>
                  <p className="text-[11px] text-muted-foreground/60 mt-2">You can change roles at any time.</p>
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="submit-member-btn">Send Invitation</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {members.map((member) => {
          const role = getRole(member);
          const memberId = member.user_id || member.user?.id;
          const RoleIcon = isPatientRole(role) ? Heart : isCarePartnerRole(role) ? Shield : Users;
          return (
            <Card key={member.id} className="surface-card" data-testid={`member-${member.id}`}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                    {member.user?.name?.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">{member.user?.name}</h4>
                        <p className="text-sm text-muted-foreground">{member.user?.email}</p>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white rounded-full ${getRoleColor(member)}`}>
                            <RoleIcon className="w-3 h-3" /> {getRoleLabel(member)}
                          </span>
                          {member.paused_until && new Date(member.paused_until) > new Date() && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-amber-700 bg-amber-100 dark:text-amber-300 dark:bg-amber-900/30 rounded-full">
                              Away until {new Date(member.paused_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">Joined {new Date(member.joined_at || member.created_at || Date.now()).toLocaleDateString()}</p>
                        {careProfiles[memberId] && (
                          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`care-profile-badge-${memberId}`}>
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300"
                              title={`Care Profile: ${careProfiles[memberId].primary}${careProfiles[memberId].current_priority ? ` · This week: ${careProfiles[memberId].current_priority}` : ''}`}>
                              {careProfiles[memberId].primary}
                            </span>
                            {careProfiles[memberId].current_priority && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                Needs: {careProfiles[memberId].current_priority}
                              </span>
                            )}
                          </div>
                        )}
                        {canManage && !isPatientRole(role) && memberId !== user.id && (
                          <div className="mt-3 max-w-[180px]">
                            <Select
                              value={isCarePartnerRole(role) ? 'care_partner' : 'member'}
                              onValueChange={(v) => handleRoleChange(member, v)}
                              disabled={updatingRole === memberId}
                            >
                              <SelectTrigger className="h-8 text-xs rounded-lg" data-testid={`member-role-select-${memberId}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="care_partner">Care Partner</SelectItem>
                                <SelectItem value="member">Supporter</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                      {member.user_id !== circle?.created_by && canManage && (
                        <Button variant="ghost" size="icon" onClick={() => handleRemoveMember(member.id)} className="text-destructive hover:bg-destructive/10" data-testid={`remove-member-${member.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default CircleMembersTab;
